import { useMemo, useState } from 'react';
import {
  CARE_AMOUNTS,
  FAMILY_HELP_OPTIONS,
  careAdjustmentsChange,
  careAdjustmentsOf,
  ESTIMATED_CARE,
  isCustomCare,
  type CareAdjustments,
  type CareAmount,
  type FamilyHelp,
} from '@shared/consumerPlan';
import { useCarePlanEditor } from './use-care-plan-editor';
import { useConsumerClient } from './use-consumer-client';

function isSameAdjustments(a: CareAdjustments, b: CareAdjustments): boolean {
  return a.careAmount === b.careAmount && a.familyHelp === b.familyHelp;
}

/**
 * The consumer's two optional care overrides, previewed live against the model and saved only
 * when they continue. Each option carries the weekly hours it would mean, so the choices show
 * real numbers rather than relative words.
 */
export function useCareAdjustments(clientId: string) {
  const { client, scenario: savedScenario } = useConsumerClient(clientId);
  const { previewScenario, saveScenario } = useCarePlanEditor(client);
  const saved = useMemo(() => (client ? careAdjustmentsOf(client) : ESTIMATED_CARE), [client]);
  const [adjustments, setAdjustments] = useState<CareAdjustments>(saved);

  const scenario = useMemo(
    () =>
      isSameAdjustments(adjustments, saved) ? savedScenario : previewScenario(careAdjustmentsChange(adjustments)),
    [adjustments, saved, savedScenario, previewScenario],
  );

  const careHoursByAmount = useMemo(
    () =>
      Object.fromEntries(
        CARE_AMOUNTS.map(careAmount => [
          careAmount,
          previewScenario(careAdjustmentsChange({ ...adjustments, careAmount }))?.careHoursPerWeek ?? 0,
        ]),
      ) as Record<CareAmount, number>,
    [adjustments, previewScenario],
  );

  const familyHoursByHelp = useMemo(
    () =>
      Object.fromEntries(
        FAMILY_HELP_OPTIONS.map(familyHelp => [
          familyHelp,
          previewScenario(careAdjustmentsChange({ ...adjustments, familyHelp }))?.familyHoursPerWeek ?? 0,
        ]),
      ) as Record<FamilyHelp, number>,
    [adjustments, previewScenario],
  );

  function saveAdjustments() {
    if (!isSameAdjustments(adjustments, saved)) saveScenario(careAdjustmentsChange(adjustments));
  }

  return {
    scenario,
    adjustments,
    isCustom: isCustomCare(adjustments),
    careHoursByAmount,
    familyHoursByHelp,
    setCareAmount: (careAmount: CareAmount) => setAdjustments(current => ({ ...current, careAmount })),
    setFamilyHelp: (familyHelp: FamilyHelp) => setAdjustments(current => ({ ...current, familyHelp })),
    saveAdjustments,
  };
}
